import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Globe,
  AlertTriangle,
  Check,
  X,
  Trash2,
  ChevronRight,
  Loader2,
  Mail,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/sponsors — sponsors (corporate accounts) awaiting a human decision.
 * Populated from `corporate_profiles` where ai_flag_status == 'flagged' OR
 * store_active == false. Admin can Approve (activates the store), Suspend
 * (keeps the account, disables the storefront), or Delete (removes the
 * corporate profile + soft-deletes the user).
 */
const AdminSponsors = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sponsors/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.sponsors || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sponsors");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (corp, action) => {
    if (action === "delete") {
      if (!window.confirm(`Permanently delete sponsor ${corp.company_name}? This soft-deletes the user account and removes their products.`)) return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/corporate/${corp.corporate_id}/decide?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(action === "approve" ? "Sponsor approved & store activated" : action === "suspend" ? "Storefront suspended" : "Sponsor deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-sponsors-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            data-testid="sponsors-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">Admin · Sponsors</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#EC008C]" />
            <span className="text-sm text-[#8A92A0]" data-testid="sponsors-count">{rows.length} pending</span>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Sponsors awaiting review</h1>
          <p className="text-[#8A92A0] max-w-2xl">
            Corporate accounts flagged by AI screening or whose storefront is not yet active. Approve to unlock the storefront, suspend to keep the account but hide the store, or delete to remove entirely.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#EC008C]" /></div>
        ) : rows.length === 0 ? (
          <div className="card p-16 text-center" data-testid="sponsors-empty">
            <Building2 className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Inbox zero</h3>
            <p className="text-[#8A92A0] text-sm">No sponsors pending right now.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((c) => (
              <div key={c.corporate_id} className="card p-5" data-testid={`sponsor-row-${c.corporate_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-white font-bold text-lg">{c.company_name}</p>
                      {c.ai_flag_status === "flagged" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#EC008C]/20 text-[#EC008C] text-[10px] font-bold uppercase tracking-widest">
                          <AlertTriangle className="w-3 h-3" /> AI flag
                        </span>
                      )}
                      {c.store_active === false && (
                        <span className="px-2 py-0.5 rounded bg-[#EBB84C]/20 text-[#EBB84C] text-[10px] font-bold uppercase tracking-widest">
                          Storefront off
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#8A92A0]">{c.contact_name} · <span className="font-mono">{c.business_email}</span></p>
                    {c.business_website && (
                      <p className="text-xs text-[#8A92A0] mt-1 flex items-center gap-1"><Globe className="w-3 h-3" /> {c.business_website}</p>
                    )}
                    {c.ai_flag_reason && (
                      <p className="text-xs text-[#FF8888] mt-2 leading-relaxed bg-[#EC008C]/5 border border-[#EC008C]/20 rounded p-2">
                        <strong>AI reason:</strong> {c.ai_flag_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => decide(c, "approve")}
                      disabled={busy}
                      className="h-9 px-3 rounded-full bg-[#8DB87A] text-black text-xs font-bold flex items-center gap-1 hover:bg-[#7EAB6B] disabled:opacity-50"
                      data-testid={`approve-sponsor-${c.corporate_id}`}
                    >
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button
                      onClick={() => decide(c, "suspend")}
                      disabled={busy}
                      className="h-9 px-3 rounded-full border border-[#EBB84C]/60 text-[#EBB84C] text-xs font-bold flex items-center gap-1 hover:bg-[#EBB84C]/10 disabled:opacity-50"
                      data-testid={`suspend-sponsor-${c.corporate_id}`}
                    >
                      <X className="w-3 h-3" /> Suspend
                    </button>
                    <button
                      onClick={() => decide(c, "delete")}
                      disabled={busy}
                      className="h-9 px-3 rounded-full border border-[#4A5260] text-[#8A92A0] text-xs font-bold flex items-center gap-1 hover:bg-[#EC008C]/10 hover:border-[#EC008C]/60 hover:text-[#EC008C] disabled:opacity-50"
                      data-testid={`delete-sponsor-${c.corporate_id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                    <Link
                      to={`/dashboard/admin-inmail?source=corporate_signup`}
                      className="h-9 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] text-xs font-bold flex items-center gap-1 hover:text-white"
                      data-testid={`view-inmail-${c.corporate_id}`}
                    >
                      <Mail className="w-3 h-3" /> View In-Mail <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSponsors;
